import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { User, UserFormData, BulkUploadResult } from '../types';
import { APIError } from '../types';

interface UserFormModalProps {
  user: User | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const emptyForm: UserFormData = {
  username: '',
  email: '',
  phone_number: '',
  password: '',
};

const UserFormModal: React.FC<UserFormModalProps> = ({ user, onClose, onSaved }) => {
  const isEdit = !!user;
  const [form, setForm] = useState<UserFormData>(
    user
      ? { username: user.username, email: user.email, phone_number: user.phone_number || '', password: '' }
      : emptyForm
  );
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof UserFormData, string>>>({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof UserFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof UserFormData, string>> = {};
    const username = form.username.trim();
    const email = form.email.trim();
    const phone = form.phone_number.trim();

    if (!username) {
      errors.username = 'Username is required';
    } else if (username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (username.length > 50) {
      errors.username = 'Username must be at most 50 characters';
    }

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!phone) {
      errors.phone_number = 'Phone number is required';
    } else if (!/^\+?\d{10,15}$/.test(phone)) {
      errors.phone_number = 'Phone number must be 10-15 digits';
    }

    if (form.password && form.password.length > 0 && form.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    setSaving(true);
    try {
      const payload: Partial<UserFormData> = {
        username: form.username.trim(),
        email: form.email.trim(),
        phone_number: form.phone_number.trim(),
      };
      if (form.password) {
        payload.password = form.password;
      }

      if (isEdit && user) {
        await authAPI.updateUser(user.id, payload);
        onSaved('Student updated successfully');
      } else {
        await authAPI.createUser(payload as UserFormData);
        onSaved('Student created successfully');
      }
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      if (apiError?.errors && apiError.errors.length > 0) {
        setServerError(apiError.errors.map((e2) => e2.msg).join(', '));
      } else {
        setServerError(apiError?.error || `Failed to ${isEdit ? 'update' : 'create'} student`);
      }
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (hasError?: string) =>
    `input-premium ${hasError ? 'border-bmlc-maroon-400' : ''}`;

  return (
    <div className="modal-overlay">
      {/* Overlay */}
      <div className="fixed inset-0" onClick={onClose}></div>

      {/* Modal */}
      <div className="modal-panel max-w-md">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div className="flex items-center space-x-2.5">
              <div className="w-9 h-9 rounded-lg bg-bmlc-navy-50 ring-1 ring-bmlc-navy-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-bmlc-navy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isEdit ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  )}
                </svg>
              </div>
              <h2 className="font-serif text-lg sm:text-xl font-bold text-bmlc-navy-900">
                {isEdit ? 'Edit Student' : 'Add Student'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost p-1.5 rounded-lg flex-shrink-0"
              title="Close"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {serverError && (
            <div className="toast-error mb-3 sm:mb-4">
              <span className="text-sm sm:text-base">⚠️</span>
              <p className="text-xs sm:text-sm">{serverError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Username <span className="text-bmlc-maroon-500">*</span>
              </label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="Student full name"
                className={inputClass(fieldErrors.username)}
              />
              {fieldErrors.username && (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Email <span className="text-bmlc-maroon-500">*</span>
              </label>
              <input
                type="text"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="student@example.com"
                className={inputClass(fieldErrors.email)}
              />
              {fieldErrors.email && (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Phone Number <span className="text-bmlc-maroon-500">*</span>
              </label>
              <input
                type="text"
                value={form.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                placeholder="9876543210"
                className={inputClass(fieldErrors.phone_number)}
              />
              {fieldErrors.phone_number && (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.phone_number}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">Password</label>
              <input
                type="password"
                value={form.password || ''}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={isEdit ? '••••••' : 'Leave blank to use phone number as password'}
                className={inputClass(fieldErrors.password)}
              />
              {fieldErrors.password ? (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.password}</p>
              ) : (
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">
                  {isEdit
                    ? 'Leave blank to keep current password'
                    : 'Leave blank to use phone number as password'}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end space-x-2 sm:space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`btn-primary px-6 py-2 rounded-lg text-white font-semibold text-xs sm:text-sm inline-flex items-center space-x-2 ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>{isEdit ? 'Save Changes' : 'Add Student'}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  // Committed search term: fetches key off this, not the input value, so typing
  // doesn't fire a request per keystroke from a stale page
  const [activeSearch, setActiveSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeSearch]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const showSuccess = (message: string) => {
    setSuccess(message);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = setTimeout(() => setSuccess(''), 4000);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authAPI.getAllUsers(currentPage, 20, activeSearch);
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Both updates batch into one render, so the effect fires a single fetch
    // with page 1 and the new term
    setActiveSearch(search);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const openAddModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleModalSaved = (message: string) => {
    setModalOpen(false);
    setEditingUser(null);
    showSuccess(message);
    fetchUsers();
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete "${user.username}"? Their chat history will also be removed.`)) return;

    try {
      setError('');
      await authAPI.deleteUser(user.id);
      showSuccess('Student deleted successfully');
      // If that was the last row on this page, step back so the admin isn't
      // stranded on an empty page past the end
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchUsers();
      }
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to delete student');
    }
  };

  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const result = await authAPI.bulkUploadUsers(file);
      setUploadResult(result);
      if (result.created_count > 0) {
        fetchUsers();
      }
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to upload Excel file');
    } finally {
      setUploading(false);
      // Reset the input so the same file can be re-selected
      e.target.value = '';
    }
  };

  const handleDownloadSample = async () => {
    try {
      setDownloadingSample(true);
      setError('');
      await authAPI.downloadSampleExcel();
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to download sample file');
    } finally {
      setDownloadingSample(false);
    }
  };

  return (
    <div className="px-3 sm:px-4 py-4 sm:py-6 animate-fade-in">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white ring-2 ring-bmlc-gold-400 shadow-gold-glow overflow-hidden flex items-center justify-center flex-shrink-0">
            <img
              src="https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG"
              alt="BM Law College Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bmlc-navy-900 leading-tight">User Management</h1>
            <p className="text-xs sm:text-sm text-bmlc-gold-600 font-semibold tracking-wide">BM Law College Student Directory</p>
            <p className="text-gray-600 text-[11px] sm:text-xs mt-1">
              Add, edit, and search all registered students with their login credentials.
            </p>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      {success && (
        <div className="toast-success mb-3 sm:mb-4">
          <span className="text-sm sm:text-base">✅</span>
          <p className="text-xs sm:text-sm">{success}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="card-premium p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <h2 className="section-title mb-0">Manage Students</h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={openAddModal}
              className="btn-primary px-4 py-2 rounded-lg text-white font-semibold text-xs sm:text-sm inline-flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Student</span>
            </button>
            <input
              type="file"
              accept=".xlsx"
              onChange={handleExcelUpload}
              className="hidden"
              id="excel-upload"
              disabled={uploading}
            />
            <label
              htmlFor="excel-upload"
              className={`btn-secondary px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm inline-flex items-center space-x-2 cursor-pointer ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>Upload Excel</span>
                </>
              )}
            </label>
            <button
              onClick={handleDownloadSample}
              disabled={downloadingSample}
              className={`btn-gold px-4 py-2 rounded-lg text-white font-semibold text-xs sm:text-sm inline-flex items-center space-x-2 ${
                downloadingSample ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>{downloadingSample ? 'Downloading...' : 'Download Sample'}</span>
            </button>
          </div>
        </div>

        {/* Bulk Upload Result Panel */}
        {uploadResult && (
          <div className="mt-4 space-y-3 animate-fade-in">
            <div className="toast-success justify-between">
              <p className="text-xs sm:text-sm">
                <span className="font-semibold">{uploadResult.created_count} created</span>,{' '}
                {uploadResult.failed_count} failed of {uploadResult.total_rows} rows
              </p>
              <button
                onClick={() => setUploadResult(null)}
                className="btn-ghost p-1 rounded-lg flex-shrink-0"
                title="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {uploadResult.errors.length > 0 && (
              <div className="rounded-xl border border-bmlc-maroon-200 shadow-soft-sm overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  <table className="min-w-full divide-y divide-bmlc-maroon-100">
                    <thead className="bg-bmlc-maroon-50 sticky top-0">
                      <tr>
                        <th className="px-3 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-semibold text-bmlc-maroon-600 uppercase tracking-wider w-20">
                          Row
                        </th>
                        <th className="px-3 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-semibold text-bmlc-maroon-600 uppercase tracking-wider">
                          Error
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-bmlc-maroon-100">
                      {uploadResult.errors.map((rowError, index) => (
                        <tr key={index}>
                          <td className="px-3 sm:px-4 py-1.5 text-[10px] sm:text-xs text-bmlc-maroon-600 font-mono">
                            {rowError.row}
                          </td>
                          <td className="px-3 sm:px-4 py-1.5 text-[10px] sm:text-xs text-bmlc-maroon-600">
                            {rowError.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search Section */}
      <div className="card p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="section-title">Search Students</h2>

        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, email, or phone number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-premium"
            />
          </div>
          <button
            type="submit"
            className="btn-primary px-6 py-2.5 rounded-lg text-white font-semibold text-xs sm:text-sm whitespace-nowrap"
          >
            Search
          </button>
        </form>
      </div>

      {/* Users List */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <h2 className="section-title mb-0">
            Registered Students {pagination && `(${pagination.total_users})`}
          </h2>
          <button
            onClick={fetchUsers}
            className="btn-ghost p-1.5 sm:p-2 rounded-lg flex-shrink-0"
            title="Refresh"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="toast-error mb-3 sm:mb-4">
            <span className="text-sm sm:text-base">⚠️</span>
            <p className="text-xs sm:text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="py-4 sm:py-6">
            <div className="space-y-3">
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-2/3"></div>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 text-center">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 sm:py-14">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bmlc-navy-50 ring-1 ring-bmlc-navy-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-soft-sm">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-bmlc-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <p className="text-bmlc-navy-900 font-serif font-semibold text-sm sm:text-base mb-1">No users found</p>
            <p className="text-gray-400 text-xs sm:text-sm">Try adjusting your search criteria</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="table-premium min-w-full">
                <thead>
                  <tr>
                    <th className="text-left">
                      Student Name
                    </th>
                    <th className="text-left">
                      Phone Number
                    </th>
                    <th className="text-left hidden sm:table-cell">
                      Email
                    </th>
                    <th className="text-left hidden md:table-cell">
                      Registration Date
                    </th>
                    <th className="text-left hidden lg:table-cell">
                      Login Info
                    </th>
                    <th className="text-left">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="whitespace-nowrap">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-bmlc-gold-50 ring-2 ring-bmlc-gold-300 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] sm:text-sm font-serif font-bold text-bmlc-gold-700">
                              {user.username?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="text-xs sm:text-sm font-medium text-bmlc-navy-900 truncate max-w-[120px] sm:max-w-[200px]">
                            {user.username}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs sm:text-sm text-gray-900 font-medium">{user.phone_number}</span>
                          <button
                            onClick={() => copyToClipboard(user.phone_number || '')}
                            className="btn-ghost p-1 rounded-md text-gray-400"
                            title="Copy phone number"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden sm:table-cell">
                        <div className="truncate max-w-[150px] sm:max-w-[200px]" title={user.email}>
                          {user.email}
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden md:table-cell">
                        {formatDate(user.created_at || '')}
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm hidden lg:table-cell">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-1">
                            <span className="text-gray-500">ID:</span>
                            <span className="font-mono text-bmlc-navy-800">{user.phone_number}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="text-gray-500">PW:</span>
                            <span className="font-mono text-bmlc-navy-800">{user.phone_number}</span>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="btn-ghost inline-flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="btn-danger-soft inline-flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 sm:mt-6 px-1">
                <div className="text-xs sm:text-sm text-gray-600">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_users)} of{' '}
                  {pagination.total_users} users
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={!pagination.has_prev}
                    className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-colors ${
                      pagination.has_prev
                        ? 'btn-secondary'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  <span className="badge-navy px-3">
                    Page {pagination.current_page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={!pagination.has_next}
                    className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-colors ${
                      pagination.has_next
                        ? 'btn-secondary'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <UserFormModal
          user={editingUser}
          onClose={() => {
            setModalOpen(false);
            setEditingUser(null);
          }}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
};

export default UserManagement;
